import React, { useState } from 'react';
import { supabase } from '../services/supabase';
import { Mail, Lock, LogIn, UserPlus } from 'lucide-react';
import PasswordReset from './PasswordReset';
import { useI18n } from '../i18n';

export default function Auth({ initialMode = 'login', onJoinProject }) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp, setIsSignUp] = useState(initialMode === 'signup');
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setIsSuccess(false);

    try {
      if (isSignUp) {
        const { error } = await supabase.auth.signUp({
          email,
          password,
        });
        if (error) throw error;
        setMessage(t('auth.accountCreated'));
        setIsSuccess(true);
        setIsSignUp(false);
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
      }
    } catch (error) {
      setMessage(error.message);
      setIsSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  if (showPasswordReset) {
    return <PasswordReset onBack={() => setShowPasswordReset(false)} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
      <div className="bg-white p-6 sm:p-8 rounded-xl border border-gray-200 max-w-md w-full">
        <div className="text-center mb-8">
          <div className={`inline-flex p-3 rounded-xl mb-4 ${isSignUp ? 'bg-peach-100 text-peach-600' : 'bg-lavender-100 text-lavender-600'}`}>
            {isSignUp ? <UserPlus size={28} /> : <LogIn size={28} />}
          </div>
          <h1 className="text-2xl font-semibold text-gray-900 mb-1">
            {isSignUp ? t('auth.signupTitle') : t('auth.loginTitle')}
          </h1>
          <p className="text-sm text-gray-500">
            {isSignUp ? t('auth.signupSubtitle') : t('auth.loginSubtitle')}
          </p>
        </div>

        <form onSubmit={handleAuth} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">
              {t('auth.email')}
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-3.5 text-gray-400" size={20} />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.emailPlaceholder')}
                required
                className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-lavender-400 focus:border-transparent transition-colors"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-900">
                {t('auth.password')}
              </label>
              {!isSignUp && (
                <button
                  type="button"
                  onClick={() => setShowPasswordReset(true)}
                  className="text-xs text-lavender-600 hover:text-lavender-700 font-medium hover:underline underline-offset-2 underline-offset-2 hover:underline"
                >
                  {t('auth.forgotPassword')}
                </button>
              )}
            </div>
            <div className="relative">
              <Lock className="absolute left-3 top-3.5 text-gray-400" size={20} />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('auth.passwordPlaceholder')}
                required
                minLength={6}
                className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-lavender-400 focus:border-transparent transition-colors"
              />
            </div>
          </div>

          {message && (
            <div className={`p-3 rounded-lg text-sm ${
              isSuccess ? 'bg-verde-100 border border-verde-200 text-verde-600' : 'bg-red-50 border border-red-200 text-red-700'
            }`}>
              {message}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`w-full h-10 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors ${
              'bg-gray-900 hover:bg-gray-700 text-white'
            }`}
          >
            {loading ? (
              t('common.loading')
            ) : isSignUp ? (
              <>
                <UserPlus size={20} />
                {t('auth.signupButton')}
              </>
            ) : (
              <>
                <LogIn size={20} />
                {t('auth.loginButton')}
              </>
            )}
          </button>
        </form>

        <div className="mt-6 text-center space-y-3">
          <button
            onClick={() => {
              setIsSignUp(!isSignUp);
              setMessage('');
              setIsSuccess(false);
            }}
            className="block w-full text-sm text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors hover:bg-gray-100"
          >
            {isSignUp ? t('auth.switchToLogin') : t('auth.switchToSignup')}
          </button>

          {onJoinProject && (
            <div className="pt-3 border-t border-gray-200">
              <p className="text-xs text-gray-500 mb-2">{t('auth.or')}</p>
              <button
                onClick={onJoinProject}
                className="block w-full text-sm text-gray-700 font-medium py-2 px-4 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {t('auth.haveInvite')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
